import { Button, Input, Label, Spinner } from '@1trading/react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/page-header';
import { StatsCard } from '@/components/ui/stats-card';
import { FeesChart } from '@/features/risk-fees/fees-chart';
import { FeesTable } from '@/features/risk-fees/fees-table';
import { useCollectedFees } from '@/hooks/queries/risk/use-risk-fees';
import { getErrorMessage } from '@/lib/api/error';
import type { FeesDateRange, FeesView } from '@/services/api/risk-fees';
import { daysAgo, toDateInputValue } from '@/utils/date';
import { formatEuros } from '@/utils/format';

const DEFAULT_RANGE_DAYS = 30;
const FETCH_ERROR_TOAST_ID = 'risk-fees-fetch-failed';

/** Last 30 days inclusive once the UTC day-bound rounding is applied. */
const defaultRange = (): FeesDateRange => ({
  from: toDateInputValue(daysAgo(DEFAULT_RANGE_DAYS)),
  to: toDateInputValue(new Date()),
});

const FeesPage = () => {
  const [draft, setDraft] = useState<FeesDateRange>(defaultRange);
  // Fetches once on mount with the defaults; afterwards only Update moves
  // `submitted` — date edits never refetch on their own.
  const [submitted, setSubmitted] = useState<FeesDateRange>(draft);
  const { data, isPending, isFetching, isError, error, refetch } = useCollectedFees(submitted);

  // Deliberate fix: legacy set a silent `_networkError` flag and
  // showed nothing — surface failures with a toast and keep the previous data
  // set on screen (query `data` drops to undefined when a new range errors).
  const [lastView, setLastView] = useState<FeesView | undefined>(undefined);
  useEffect(() => {
    if (data) setLastView(data);
  }, [data]);
  useEffect(() => {
    if (error) toast.error('Failed to fetch collected fees', { id: FETCH_ERROR_TOAST_ID });
  }, [error]);

  const view = data ?? lastView;

  const handleUpdate = () => {
    if (draft.from === submitted.from && draft.to === submitted.to) {
      refetch();
      return;
    }
    setSubmitted(draft);
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Fees" />
      {isPending ? (
        // Initial load only — the whole content sits behind a loading panel;
        // later refetches keep chart/table mounted via keepPreviousData.
        <div className="flex min-h-96 items-center justify-center">
          <Spinner className="size-8" />
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="fees-date-from">Date From</Label>
              <Input
                className="w-44"
                id="fees-date-from"
                onChange={(event) => setDraft((previous) => ({ ...previous, from: event.target.value }))}
                type="date"
                value={draft.from}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="fees-date-to">Date To</Label>
              <Input className="w-44" id="fees-date-to" onChange={(event) => setDraft((previous) => ({ ...previous, to: event.target.value }))} type="date" value={draft.to} />
            </div>
            <Button className="gap-2" disabled={isFetching} onClick={handleUpdate} type="button" variant="primary">
              {isFetching ? <Spinner className="size-4" /> : null}
              Update
            </Button>
          </div>
          {isError ? (
            <div className="flex items-center gap-3">
              <p className="text-sm text-red-500">Failed to fetch collected fees: {getErrorMessage(error)}</p>
              <Button onClick={() => refetch()} size="sm" type="button" variant="outline">
                Retry
              </Button>
            </div>
          ) : null}
          {view ? (
            <>
              <div className="grid gap-4 lg:grid-cols-3">
                {/* Preformatted euro string rendered verbatim — the legacy card's
                    display="integer" prop was a no-op */}
                <StatsCard label="Profit" value={formatEuros(view.profitRaw)} />
              </div>
              {/* Chart hidden on an empty result, matching legacy */}
              {view.chartData.length > 0 ? <FeesChart data={view.chartData} series={view.chartSeries} /> : null}
              <FeesTable currencies={view.tableCurrencies} rows={view.tableRows} />
            </>
          ) : null}
        </>
      )}
    </div>
  );
};
FeesPage.displayName = 'FeesPage';

export default FeesPage;
